import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../../../AxiosInstance";
import { isAdmin } from "../../../../../utils/roles";
import styles from "./alert_section.module.css";
import {
  AlertCategories,
  AlertTypes,
  alertCategory,
  alertToIcon,
  formatOutage,
  formatTimestamp,
  powerCauseToText,
  powerDetailText,
  systemCodeToText,
  typeToName,
  typeToUnit,
} from "../../../../../utils/utils";

// Qué grupos se muestran. Se recuerda por navegador. Los encendidos y
// apagados van visibles por defecto: para operación son tan críticos como
// una sobrecorriente, y el filtro existe para quien no quiera verlos.
const FILTERS_KEY = "pdui_alert_filters";
// Tamaño de página: igual al de la página en vivo que trae el detalle.
const PAGE = 30;
const DEFAULT_FILTERS = {
  [AlertCategories.DEVICE]: true,
  [AlertCategories.POWER]: true,
  [AlertCategories.SYSTEM]: true,
};

function loadFilters() {
  try {
    const raw = window.localStorage.getItem(FILTERS_KEY);
    if (!raw) return DEFAULT_FILTERS;
    return { ...DEFAULT_FILTERS, ...JSON.parse(raw) };
  } catch (e) {
    return DEFAULT_FILTERS;
  }
}

function saveFilters(filters) {
  try {
    window.localStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
  } catch (e) {
    // sin storage se pierde al recargar, nada más
  }
}

// Energía, conexión, servidor, plataforma y escalados: título, origen o
// causa, detalle libre y fecha. Sin puerto ni valor con unidad.
function EventAlert({ data, t }) {
  const isPower = data.alert_type === AlertTypes.POWER;
  const isConnection = data.alert_type === AlertTypes.CONNECTION;
  const isNetwork = data.alert_type === AlertTypes.NETWORK;
  // Batería y alternador: alertas del equipo sin puerto, con el detalle
  // redactado por el firmware (tensión, duración). Van como evento, no en
  // el formato por puerto.
  const isElectrical = data.alert_type === AlertTypes.BATTERY || data.alert_type === AlertTypes.ALTERNATOR
    || data.alert_type === AlertTypes.VIBRATION;
  // Encendido sin apagado ordenado previo: el equipo se apagó sin poder
  // avisar (corte sin UPS, reset). Se muestra como el apagado que faltó.
  const unreportedOff = isPower && data.alert_subtype === "on" && data.cause === "unexpected";
  let subtitle = "";
  let detail = data.message || "";
  if (isPower) {
    subtitle = powerCauseToText(data.cause);
    // Detalle del equipo redactado en el idioma de la interfaz; si no se
    // reconoce el formato, se muestra tal cual llegó.
    detail = powerDetailText(data.message) || data.message || "";
    if (unreportedOff && data.outage_seconds) {
      detail = `${t('alerts.unreportedDowntime', { d: formatOutage(data.outage_seconds) })}\n${detail}`;
    }
  } else if (isConnection) {
    // La nube calcula estas alertas con sus propios datos: el umbral en
    // alert_value (lost) y la duración en outage_seconds (restored).
    subtitle = t('alerts.cloudSource');
    detail =
      data.alert_subtype === "restored"
        ? t('alerts.outageDuration', { d: formatOutage(data.outage_seconds) })
        : t('alerts.connectionLostDetail', { s: Math.round(data.alert_value || 0) });
  } else if (isNetwork) {
    // Zona del mapa de cobertura que empeoró: el detalle ya viene redactado
    // por la nube con los valores de antes y después.
    subtitle = t('alerts.cloudSource');
    detail = data.message || "";
  } else if (isElectrical) {
    subtitle = t('alerts.deviceSource');
    detail = data.message || "";
  } else if (data.alert_type === AlertTypes.SERVER) {
    subtitle = `${systemCodeToText(data.alert_subtype)} · ${t('alerts.serverSource')}`;
  } else if (data.alert_type === AlertTypes.PLATFORM) {
    subtitle = `${systemCodeToText(data.alert_subtype)} · ${t('alerts.platformSource')}`;
  } else {
    subtitle = t('alerts.deviceSource');
  }
  const electricalCritical = isElectrical && (data.alert_subtype === "critical" || data.alert_subtype === "high" || data.alert_subtype === "impact");
  const isOff = (isPower && data.alert_subtype === "off") || unreportedOff || electricalCritical;
  const isWarn = (isConnection && data.alert_subtype === "lost") || isNetwork || (isElectrical && !electricalCritical);
  const tone = isOff ? styles.eventAlertOff : isWarn ? styles.eventAlertWarn : "";
  return (
    <div className={`${styles.alert} ${styles.eventAlert} ${tone}`}>
      <img
        src={alertToIcon(data.alert_type)}
        alt=""
        width={"34px"}
        height={"34px"}
      />
      <div className={styles.eventBody}>
        <div className={styles.alertTitle}>
          <div>{typeToName(data.alert_type, data.alert_subtype, data.cause)}</div>
          <div>{subtitle || "-"}</div>
        </div>
        {detail && <div className={styles.alertMessage}>{detail}</div>}
      </div>
      <div className={styles.alertStamp}>
        <div className={styles.alertDay}>
          {formatTimestamp(data.created_at)}
        </div>
        <div className={styles.alertSpacer} />
      </div>
    </div>
  );
}

function Alert({ data, lastPort, t }) {
  if (
    alertCategory(data.alert_type) !== AlertCategories.DEVICE ||
    data.alert_type === AlertTypes.NETWORK ||
    data.alert_type === AlertTypes.BATTERY ||
    data.alert_type === AlertTypes.ALTERNATOR ||
    data.alert_type === AlertTypes.VIBRATION
  ) {
    return <EventAlert data={data} t={t} />;
  }
  if (data.alert_type === 5) {
    return (
      <div className={styles.alert}>
        <img
          src={alertToIcon(data.alert_type)}
          alt={"emergency_stop"}
          width={"34px"}
          height={"34px"}
        />
        <div className={styles.alertTitle}>
          <div>{typeToName(data.alert_type, data.alert_subtype)}</div>
          <div>-</div>
        </div>
        <div>
          <div
            style={{
              width: "4px",
              height: "4px",
              backgroundColor: "#000",
              borderRadius: "50%",
            }}
          />
          <div style={{ height: "15px" }} />
        </div>
        <div>
          <div className={styles.alertDay}>
            {formatTimestamp(data.created_at)}
          </div>
          <div style={{ height: "15px" }} />
        </div>
      </div>
    );
  }
  return (
    <div className={styles.alert}>
      <img
        src={alertToIcon(data.alert_type)}
        alt={"voltage_alert"}
        width={"45px"}
        height={"45px"}
      />
      <div className={styles.alertTitle}>
        <div>{typeToName(data.alert_type, data.alert_subtype)}</div>
        {data.alert_type === AlertTypes.TEMPERATURE ? (
          <div>{t('alerts.inSensor', { n: data.port_alert })}</div>
        ) : data?.port_alert === 0 ? (
          <div>{t('alerts.inInput')}</div>
        ) : (
          <div>{t('alerts.inPort', { port: data.port_alert })}</div>
        )}
      </div>
      <div>
        <div
          style={{
            width: "4px",
            height: "4px",
            backgroundColor: "#000",
            borderRadius: "50%",
          }}
        />
        <div style={{ height: "15px" }} />
      </div>
      <div>
        <div className={styles.alertDay}>
          {formatTimestamp(data.created_at)}
        </div>
        <div style={{ height: "15px" }} />
      </div>
      {data.alert_value && (
        <div className={styles.alertValue}>
          <div>{parseFloat(data.alert_value).toFixed(0)}</div>
          <div>{typeToUnit(data.alert_type, data.alert_subtype)}</div>
        </div>
      )}
    </div>
  );
}

function AlertDate({ date }) {
  return (
    <div className={styles.alertDate}>
      <div>{date}</div>
      <div>{date}</div>
    </div>
  );
}

export default function AlertSection({ alerts, pduiData, pdui_id }) {
  const { t } = useTranslation();
  const [lastPort, setLastPort] = useState(null);
  const [filters, setFilters] = useState(loadFilters);
  // Páginas anteriores pedidas con "Ver anteriores". La página en vivo la
  // trae el detalle cada pocos segundos; aquí se juntan sin repetir.
  const [older, setOlder] = useState([]);
  const [exhausted, setExhausted] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    setLastPort(pduiData?.model.port_count);
  }, [pduiData]);

  useEffect(() => {
    setOlder([]);
    setExhausted(false);
  }, [pdui_id]);

  const seen = new Set();
  const all = [...alerts, ...older].filter((a) => {
    const key = a.id ?? `${a.created_at}-${a.alert_type}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const hasMore = !exhausted && alerts.length >= PAGE;

  const loadMore = async () => {
    const last = all[all.length - 1];
    if (!last || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await axiosInstance.get(
        `alert/?pdui_id=${pdui_id}&limit=${PAGE}&before_id=${last.id}&before=${encodeURIComponent(last.created_at)}`,
      );
      const page = r.data || [];
      setOlder((prev) => [...prev, ...page]);
      if (page.length < PAGE) setExhausted(true);
    } catch (e) {
      // se puede volver a intentar con el mismo botón
    } finally {
      setLoadingMore(false);
    }
  };

  const toggleFilter = (category) => {
    setFilters((prev) => {
      const next = { ...prev, [category]: !prev[category] };
      saveFilters(next);
      return next;
    });
  };

  // Las alertas de sistema (salud del propio equipo y de la nube: procesador,
  // disco, workers, servidor) son para quien mantiene el equipo, no para el
  // cliente que opera el camión: solo las ven los administradores.
  const admin = isAdmin();
  const allowed = (alert) => admin || alertCategory(alert.alert_type) !== AlertCategories.SYSTEM;
  const visible = all.filter((alert) => allowed(alert) && filters[alertCategory(alert.alert_type)]);

  const chips = [
    { id: AlertCategories.DEVICE, label: t('alerts.filterDevice') },
    { id: AlertCategories.POWER, label: t('alerts.filterPower') },
    ...(admin ? [{ id: AlertCategories.SYSTEM, label: t('alerts.filterSystem') }] : []),
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={"d-flex align-items-center"}>
          <img src={"/icons/alerts/alert.png"} alt={"alert"} width={"15px"} />
          <div className={"ps-2"}>{t('alerts.title')}</div>
        </div>
        <div className={styles.filters}>
          {chips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              className={`${styles.chip} ${filters[chip.id] ? styles.chipOn : ""}`}
              onClick={() => toggleFilter(chip.id)}
              title={t('alerts.filterHint')}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>
      <div className={styles.body}>
        {visible.length === 0 ? (
          <div className={styles.emptyState}>
            {all.filter(allowed).length === 0 ? t('alerts.noAlerts') : t('alerts.allFiltered')}
          </div>
        ) : (
          <div className={styles.section}>
            <div className={styles.day}>
              <AlertDate />
            </div>
            <div className={styles.alerts}>
              {visible.map((alert) => (
                <Alert lastPort={lastPort} key={alert.id ?? `${alert.created_at}-${alert.alert_type}`} data={alert} t={t} />
              ))}
            </div>
          </div>
        )}
        {(hasMore || older.length > 0) && (
          <div className={styles.loadMoreRow}>
            {hasMore ? (
              <button type="button" className={styles.loadMore} onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? t('alerts.loadingMore') : t('alerts.loadMore')}
              </button>
            ) : (
              <div className={styles.noMore}>{t('alerts.noMore')}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
